import { useCallback, useContext, useEffect, useState } from 'react';

// Hooks
import useFetchIntervalData from '../../../../packages/hooks/useFetchIntervalData';
import useSubscriptionClick from '../../../../packages/hooks/useSubscriptionClick';

// Contexts
import TennisApiContext from '../contexts/TennisApiContext';

// Types
import { ITennisConfig, ITennisMatchList, ITennisLivescoreResponseData } from '../../../../packages/types';
import useDebounce from '../../../../packages/hooks/useDebounce';
import { IGeneralSubscriberInfo } from '../../../../packages/constants/subscribeTypes';
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';

interface IUseTennisLivescoreArgs extends IGeneralSubscriberInfo {
  dataRefreshTime?: ITennisConfig['dataRefreshTime'];
  dataOddsDisplay: ITennisConfig['dataOddsDisplay'];
  dataOddsMarket: ITennisConfig['dataOddsMarket'];
  dataOddsMarketValueType: ITennisConfig['dataOddsMarketValueType'];
  dataOddsBettingId: ITennisConfig['dataOddsBettingId'];
  dataOddsPreEventOnly: ITennisConfig['dataOddsPreEventOnly'];
  dateFilter: Date;
  statusFilter: string;
  dataPopularList?: ITennisConfig['dataPopularList'];
}

export default function useLivescore({
  subscribers,
  dataRefreshTime,
  dataWidgetId,
  dataWidgetSport,
  dateFilter,
  statusFilter,
  dataOddsDisplay,
  dataOddsMarket,
  dataOddsMarketValueType,
  dataOddsPreEventOnly,
  dataOddsBettingId,
  dataPopularList,
  dataWidgetCuid,
}: IUseTennisLivescoreArgs) {
  const { $tennis } = useContext(TennisApiContext);
  const [livescore, setLivescore] = useState<ITennisMatchList[]>([]);
  const debouncedDateFilter = useDebounce(dateFilter, 300);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchLivescore = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      manageLoader && setIsLoading(true);
      $tennis
        ?.getLivescore({
          dataOddsMarket,
          dataOddsMarketValueType,
          dataOddsBettingId,
          dateFilter: debouncedDateFilter,
          statusFilter,
          dataOddsDisplay,
          dataPopularList,
          dataOddsPreEventOnly,
        })
        ?.then(({ matches }: ITennisLivescoreResponseData) => {
          manageLoader && setIsLoading(false);
          setLivescore(matches);
          !isFromInterval && stackOdds(matches, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
        })
        .catch((error: unknown) => {
          console.error(error);
        });
    },
    [
      $tennis,
      dataOddsBettingId,
      dataOddsDisplay,
      dataOddsMarket,
      dataOddsMarketValueType,
      dataOddsPreEventOnly,
      dataPopularList,
      debouncedDateFilter,
      statusFilter,
      dataWidgetCuid,
      dataWidgetId,
      dataWidgetSport,
    ],
  );

  const refetchWidgetData = useFetchIntervalData(fetchLivescore, dataRefreshTime);

  useEffect(() => {
    fetchLivescore(true);
  }, [fetchLivescore]);

  return {
    showWidget: true,
    widgetData: { livescore, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
    refetchWidgetData,
  };
}
